import { axiosAzureInstance } from '../Utils/axios';
import type { AxiosResponse } from 'axios';

const API_BASE_URL = '/uaa/api/observability/metrics';

interface MetricsParams {
  namespace?: string;
  service?: string;
  period?: string;
  from?: string | null;
  to?: string | null;
  [key: string]: string | null | undefined;
}

const ServicesMonitorService = {
  /**
   * Get health for a service via UAA proxy. service = Eureka serviceId ('cspservice', 'uaa', 'ihub-adapter').
   */
  getHealth(service: string): Promise<AxiosResponse> {
    const serviceParam = service && service.trim() ? service.trim() : 'uaa';
    return axiosAzureInstance.get('/uaa/api/observability/health', {
      params: { service: serviceParam },
    });
  },

  /**
   * Get list of API paths for a service via UAA proxy. service = Eureka serviceId ('cspservice' for CSP).
   */
  getApiEndpoints(service: string): Promise<{ data: string[] }> {
    const serviceParam = service && service.trim() ? service.trim() : 'uaa';
    return axiosAzureInstance
      .get('/uaa/api/observability/endpoints', { params: { service: serviceParam } })
      .then(response => ({
        data: Array.isArray(response.data) ? response.data : [],
      }));
  },

  getMetricList(namespace = '', service: string | null = null): Promise<AxiosResponse> {
    const params: MetricsParams = namespace ? { namespace } : {};
    if (service) params.service = service;
    return axiosAzureInstance.get(`${API_BASE_URL}/list`, { params });
  },

  getHttpMetrics(
    period = '24h',
    service: string | null = null,
    from: string | null = null,
    to: string | null = null
  ): Promise<AxiosResponse> {
    const params: MetricsParams = { period };
    if (service) params.service = service;
    if (from) params.from = from;
    if (to) params.to = to;
    return axiosAzureInstance.get(`${API_BASE_URL}/http`, { params });
  },

  getHttpEndpointMetrics(
    uri: string,
    period = '24h',
    from: string | null = null,
    to: string | null = null,
    service: string | null = null,
    client: string | null = null
  ): Promise<AxiosResponse> {
    const params: MetricsParams = { uri, period };
    if (from) params.from = from;
    if (to) params.to = to;
    if (service) params.service = service;
    if (client && client !== 'all') params.client = client;
    return axiosAzureInstance.get(`${API_BASE_URL}/http/endpoint`, { params });
  },

  /**
   * Get available client IDs for a service via UAA metadata endpoint.
   * UAA queries CloudWatch metrics for all clients that have generated traffic.
   * service = 'adv', period = '24h', '7d', etc.
   * Returns list of client IDs that have sent requests to the service.
   */
  getAvailableClients(service = 'adv', period = '24h') {
    const serviceParam = service && service.trim() ? service.trim() : 'adv';
    const params = { period, service: serviceParam };
    // Query UAA metadata endpoint - UAA handles CloudWatch metric retrieval
    return axiosAzureInstance
      .get('/uaa/api/observability/metrics/metadata/clients', { params })
      .then(response => ({
        data: Array.isArray(response.data) ? response.data : [],
      }))
      .catch(error => {
        console.warn(`Failed to fetch available clients from UAA for ${serviceParam}:`, error);
        // Return empty array on error (will use fallback in component)
        return { data: [] };
      });
  },
};

export default ServicesMonitorService;
